/**
 * Every patient-facing string of the signature re-capture page (PF-6119), in one file.
 *
 * This page exists because the app's signature screen never told the patient what they were signing
 * — its copy still reads "Letzter Schritt! Bitte bestätige deine gemachten Angaben per Unterschrift"
 * to someone who onboarded months ago. Copy is the point of the exercise here, not decoration, so it
 * gets one reviewable file rather than being spread across three screens.
 *
 * German by design: this is what a patient reads.
 */
export const signatureRecaptureCopy = {
  phone: {
    // The context lives here rather than on the signing screen: this is the first thing the patient
    // sees, and arriving at a signature pad without knowing why is what the app's own screen got
    // wrong. Screen 2 then only has to label the pad, which leaves it room for the document.
    title: 'Bitte unterschreibe deine Abtretungserklärung',
    body:
      'Deine bisherige Unterschrift war leider nicht lesbar, deshalb konnte deine Krankenkasse die ' +
      'Kostenerstattung nicht bearbeiten. Bestätige zum Schutz deiner Daten zuerst deine Handynummer.',
    countryLabel: 'Land',
    nationalLabel: 'Telefonnummer*',
    forwardLabel: 'Bestätigen',
    /** Deliberately identical for an unknown id, a completed target and a wrong number. */
    mismatch: 'Leider stimmt deine Handynummer nicht mit unseren Daten überein. Versuche es bitte erneut.',
    mismatchRepeated:
      'Leider stimmt deine Handynummer nicht mit unseren Daten überein. Versuche es bitte erneut oder kontaktiere uns.',
    /** Shown after two failed attempts. No Chatwoot widget on this standalone route — the patient
     *  already has an open mail thread from the link they were sent. */
    supportHint: 'Antworte einfach auf die E-Mail, die du von uns erhalten hast.',
  },
  sign: {
    // Deliberately short — the why was already explained on screen 1, and the document plus the pad
    // need the vertical space on a phone.
    title: 'Deine Unterschrift',
    body: 'Bitte sieh dir das Dokument an und unterschreibe darunter.',
    signatureLabel: 'Deine Unterschrift',
    forwardLabel: 'Unterschrift senden',
    documentFallbackNotice: 'Dein Browser kann das Dokument hier nicht anzeigen.',
    documentFallbackLink: 'Dokument in neuem Tab öffnen',
    inkError: 'Bitte unterschreibe etwas größer und vollständiger.',
    submitError: 'Deine Unterschrift konnte nicht gespeichert werden. Bitte versuche es erneut.',
  },
  /** Shown while the link's status is being resolved, before anything else can be decided. */
  checkingLink: {
    title: 'Einen Moment bitte',
    body: 'Wir prüfen deinen Link.',
  },
  /** Shown instead of the phone screen when the link has already been used. */
  alreadyCompleted: {
    title: 'Das hast du schon erledigt',
    body:
      'Deine Unterschrift liegt uns bereits vor — du musst nichts weiter tun. Wir melden uns bei dir, ' +
      'falls wir doch noch etwas brauchen.',
  },
  /** Shown for an unknown, revoked or missing id, and when the status check itself fails. */
  invalidLink: {
    title: 'Dieser Link ist nicht mehr gültig',
    body:
      'Bitte nutze den Link aus unserer letzten Nachricht an dich. Falls das nicht klappt, antworte ' +
      'einfach auf diese Nachricht — wir schicken dir einen neuen.',
  },
  success: {
    /** `{firstName}` is substituted by the screen. */
    title: 'Vielen Dank, {firstName}!',
    body:
      'Deine Unterschrift ist bei uns eingegangen. Wir kümmern uns um alles Weitere und melden uns bei ' +
      'dir, falls wir noch etwas brauchen. Du musst nichts weiter tun.',
  },
} as const;
